import React, { useState, useEffect, useCallback } from 'react';
import { MasterDataService } from '../../Services/MasterDataService';
import { getEndpointConfig } from '../../constants/masterDataEndpoints';

const requestCache = new Map();

const createCacheKey = (dataType, page, params, searchTerm = '') => {
  const paramString = params ? JSON.stringify(params) : '';
  const searchString = searchTerm ? `_search_${searchTerm}` : '';
  return `${dataType}_${page}_${paramString}${searchString}`;
};

export const useMasterData = (dataType, options = {}) => {
  const { loadAll = false, params = {}, enableInfiniteScroll = false } = options;
  
  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Prevent duplicate calls
  const isLoadingRef = React.useRef(false);
  const hasLoadedRef = React.useRef(false);

  const endpointConfig = getEndpointConfig(dataType);

  // Get service function based on dataType
  const getServiceFunction = useCallback((dataType, page, limit, params, searchTerm = '') => {
    // If search term is provided, use search functions
    if (searchTerm && searchTerm.trim()) {
      const searchServiceMap = {
        languages: () => MasterDataService.searchLanguages(searchTerm, page, limit),
        maritalStatuses: () => MasterDataService.searchMaritalStatuses(searchTerm, page, limit),
        religions: () => MasterDataService.searchReligions(searchTerm, page, limit),
        castes: () => MasterDataService.searchCastes(searchTerm, page, limit, params.divisionCode),
        heights: () => MasterDataService.searchHeights(searchTerm, page, limit),
        birthStars: () => MasterDataService.searchBirthStars(searchTerm, page, limit),
        jobs: () => MasterDataService.searchJobs(searchTerm, page, limit),
        educations: () => MasterDataService.searchEducations(searchTerm, page, limit),
        interests: () => MasterDataService.searchInterests(searchTerm, page, limit),
        weights: () => MasterDataService.searchWeights(searchTerm, page, limit),
        manglikLists: () => MasterDataService.searchManglikLists(searchTerm, page, limit),
        food: () => MasterDataService.searchFood(searchTerm, page, limit),
        drinking: () => MasterDataService.searchDrinking(searchTerm, page, limit),
        smoking: () => MasterDataService.searchSmoking(searchTerm, page, limit),
        ideologies: () => MasterDataService.searchIdeologies(searchTerm, page, limit),
        familyTypes: () => MasterDataService.searchFamilyTypes(searchTerm, page, limit)
      };
      return searchServiceMap[dataType] || (() => Promise.reject(new Error(`Unknown data type: ${dataType}`)));
    }

    // Regular service functions
    const serviceMap = {
      languages: () => MasterDataService.getLanguages(page, limit),
      maritalStatuses: () => MasterDataService.getMaritalStatuses(page, limit),
      religions: () => MasterDataService.getReligions(page, limit),
      castes: () => MasterDataService.getCastes(page, limit, params.divisionCode),
      heights: () => MasterDataService.getHeights(page, limit),
      birthStars: () => MasterDataService.getBirthStars(page, limit),
      jobs: () => MasterDataService.getJobs(page, limit),
      educations: () => MasterDataService.getEducations(page, limit),
      interests: () => MasterDataService.getInterests(page, limit),
      weights: () => MasterDataService.getWeights(page, limit),
      manglikLists: () => MasterDataService.getManglikLists(page, limit),
      food: () => MasterDataService.getFood(page, limit),
      drinking: () => MasterDataService.getDrinking(page, limit),
      smoking: () => MasterDataService.getSmoking(page, limit),
      ideologies: () => MasterDataService.getIdeologies(page, limit),
      familyTypes: () => MasterDataService.getFamilyTypes(page, limit)
    };

    return serviceMap[dataType] || (() => Promise.reject(new Error(`Unknown data type: ${dataType}`)));
  }, []);

  // Fetch data for a specific page
  const fetchPage = useCallback(async (page = 1, append = false) => {
    if (!dataType || !endpointConfig || isLoadingRef.current) {
      return;
    }

    // Create cache key for this request
    const cacheKey = createCacheKey(dataType, page, params, searchTerm);
    
    // Check if we already have this data cached
    if (requestCache.has(cacheKey)) {
      const cachedResponse = requestCache.get(cacheKey);
      const newData = cachedResponse?.data || cachedResponse || [];
      const count = cachedResponse?.total || cachedResponse?.count || (Array.isArray(cachedResponse) ? cachedResponse.length : 0);
      
      if (Array.isArray(newData)) {
        if (append) {
          setData(prev => [...prev, ...newData]);
        } else {
          setData(newData);
          setTotalCount(count);
        }
        setCurrentPage(page);
      }
      return;
    }

    isLoadingRef.current = true;
    
    // Set appropriate loading state
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const serviceFunction = getServiceFunction(dataType, page, endpointConfig.defaultLimit, params, searchTerm);
      const response = await serviceFunction();

      // Cache the response
      requestCache.set(cacheKey, response);

      // Extract data and count from API response
      const newData = response?.data || response || [];
      const count = response?.total || response?.count || (Array.isArray(response) ? response.length : 0);

      if (Array.isArray(newData)) {
        if (append) {
          // Append new data to existing data
          setData(prev => [...prev, ...newData]);
          setHasMore(data.length + newData.length < count);
        } else {
          // Replace data and set total count
          setData(newData);
          setTotalCount(count);
          setHasMore(newData.length < count);
        }
        setCurrentPage(page);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }, [dataType, endpointConfig, getServiceFunction, params, searchTerm, data.length]);

  // Load all data at once
  const loadAllData = useCallback(async () => {
    if (!dataType || !endpointConfig || isLoadingRef.current) return;

    // Create cache key for "all" data
    const cacheKey = createCacheKey(dataType, 'all', params, searchTerm);
    
    // Check if we already have this data cached
    if (requestCache.has(cacheKey)) {
      const cachedResponse = requestCache.get(cacheKey);
      const allData = cachedResponse?.data || cachedResponse || [];
      
      if (Array.isArray(allData)) {
        setData(allData);
        setTotalCount(cachedResponse?.total || cachedResponse?.count || allData.length);
        setCurrentPage(1);
      }
      return;
    }

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const serviceMap = {
        languages: () => MasterDataService.getAllLanguages(),
        maritalStatuses: () => MasterDataService.getAllMaritalStatuses(),
        religions: () => MasterDataService.getAllReligions(),
        castes: () => MasterDataService.getAllCastes(params.divisionCode),
        heights: () => MasterDataService.getAllHeights(),
        birthStars: () => MasterDataService.getAllBirthStars(),
        jobs: () => MasterDataService.getAllJobs(),
        educations: () => MasterDataService.getAllEducations(),
        interests: () => MasterDataService.getAllInterests(),
        weights: () => MasterDataService.getAllWeights(),
        manglikLists: () => MasterDataService.getAllManglikLists(),
        food: () => MasterDataService.getAllFood(),
        drinking: () => MasterDataService.getAllDrinking(),
        smoking: () => MasterDataService.getAllSmoking(),
        ideologies: () => MasterDataService.getAllIdeologies(),
        familyTypes: () => MasterDataService.getAllFamilyTypes()
      };

      const serviceFunction = serviceMap[dataType];
      if (!serviceFunction) {
        throw new Error(`Unknown data type: ${dataType}`);
      }

      const response = await serviceFunction();
      
      // Cache the response
      requestCache.set(cacheKey, response);
      
      // Extract data from API response - handle both direct array and wrapped response
      const allData = response?.data || response || [];
      
      if (Array.isArray(allData)) {
        setData(allData);
        setTotalCount(response?.total || response?.count || allData.length);
        setCurrentPage(1);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [dataType, endpointConfig, params, searchTerm]);

  // Simple loadMore function - the core pagination logic
  const loadMore = useCallback(() => {
    // Check if we can load more data
    const canLoadMore = !isLoadingRef.current && hasMore && !loadingMore;
    
    if (canLoadMore) {
      fetchPage(currentPage + 1, true);
    }
  }, [hasMore, currentPage, fetchPage, loadingMore]);

  // Search function
  const search = useCallback((newSearchTerm) => {
    // Update search term state
    setSearchTerm(newSearchTerm);
    
    // Clear existing data and reset state
    setData([]);
    setCurrentPage(1);
    setTotalCount(0);
    setError(null);
    setHasMore(true);
    hasLoadedRef.current = false;
    isLoadingRef.current = false;
    
    // Trigger new search or load regular data
    if (newSearchTerm && newSearchTerm.trim()) {
      // Use search endpoint
      fetchPage(1, false);
    } else {
      // If search term is empty, load regular data (first 10 items)
      if (loadAll) {
        loadAllData();
      } else {
        // Reset to normal pagination - load first page
        fetchPage(1, false);
      }
    }
  }, [loadAll, loadAllData, fetchPage]);

  // Refresh data
  const refresh = useCallback(() => {
    if (loadAll) {
      loadAllData();
    } else {
      fetchPage(1, false);
    }
  }, [loadAll, loadAllData, fetchPage]);

  // Initialize data on mount
  useEffect(() => {
    if (!hasLoadedRef.current && !loading && dataType && endpointConfig) {
      hasLoadedRef.current = true;
      if (loadAll) {
        loadAllData();
      } else {
        fetchPage(1, false);
      }
    }
  }, [dataType, loadAll, loading, endpointConfig, loadAllData, fetchPage]);

  // Reset state when dataType changes
  useEffect(() => {
    setData([]);
    setCurrentPage(1);
    setTotalCount(0);
    setError(null);
    setHasMore(true);
    hasLoadedRef.current = false;
    isLoadingRef.current = false;
  }, [dataType]);

  // Infinite scroll functionality
  const handleScroll = useCallback((event) => {
    if (!enableInfiniteScroll || !hasMore || loadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    // Trigger load more when user scrolls to 60% of the content
    if (scrollPercentage >= 0.6) {
      loadMore();
    }
  }, [enableInfiniteScroll, hasMore, loadingMore, loadMore]);

  // Clear cache
  const clearCache = useCallback(() => {
    const keysToDelete = Array.from(requestCache.keys()).filter(key => key.startsWith(dataType));
    keysToDelete.forEach(key => requestCache.delete(key));
  }, [dataType]);

  return {
    data,
    loading,
    loadingMore,
    error,
    totalCount,
    currentPage,
    hasMore,
    fetchPage,
    loadMore,
    refresh,
    loadAllData,
    clearCache,
    handleScroll,
    search,
    searchTerm,
    endpointConfig
  };
};

export const clearAllMasterDataCache = () => {
  requestCache.clear();
};

export const getMasterDataCacheStatus = () => {
  return {
    cachedRequests: requestCache.size,
    cacheKeys: Array.from(requestCache.keys())
  };
};
